import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
// import isNil from 'lodash/isNil';

import { SIZES_WITH_RESPONSIVE_OPTION } from './types';

function sizeToClass(size) {
    switch (size) {
        case 'none':
            return '0';

        case 'gutter':
            return '3';

        case 'xxs':
            return 'half';

        case 'xs':
            return '1';

        case 'sm':
            return 'one-and-half';

        case 'md':
            return 'two-and-half';

        case 'lg':
            return '4';

        case 'xl':
            return '6';

        case 'xxl':
            return '8';

        default:
            return '0';
    }
}

function indexToBreakpoint(index) {
    switch (index) {
        case 0:
            return '';

        case 1:
            return '-md';

        case 2:
            return '-xl';

        default:
            return '';
    }
}

export default function Box({
    tag = 'div',
    children,
    className,
    padding,
    paddingX,
    paddingY,
    margin,
    marginX,
    marginY,
    background,
    shadow,
    border,
    alignX,
    alignY,
    textAlign,
    innerRef,
    ...rest
}) {
    const Tag = tag;
    const classes = {};

    // padding
    if (padding && Array.isArray(padding)) {
        padding.forEach((item, index) => {
            classes[`p${indexToBreakpoint(index)}-${sizeToClass(item)}`] = true;
        });
    } else if (padding) {
        classes[`p-${sizeToClass(padding)}`] = true;
    }

    // paddingX
    if (paddingX && Array.isArray(paddingX)) {
        paddingX.forEach((item, index) => {
            classes[`pl${indexToBreakpoint(index)}-${sizeToClass(item)}`] = true;
            classes[`pr${indexToBreakpoint(index)}-${sizeToClass(item)}`] = true;
        });
    } else if (paddingX) {
        classes[`pl-${sizeToClass(paddingX)}`] = true;
        classes[`pr-${sizeToClass(paddingX)}`] = true;
    }

    // paddingY
    if (paddingY && Array.isArray(paddingY)) {
        paddingY.forEach((item, index) => {
            classes[`pt${indexToBreakpoint(index)}-${sizeToClass(item)}`] = true;
            classes[`pb${indexToBreakpoint(index)}-${sizeToClass(item)}`] = true;
        });
    } else if (paddingY) {
        classes[`pt-${sizeToClass(paddingY)}`] = true;
        classes[`pb-${sizeToClass(paddingY)}`] = true;
    }

    // margin
    if (margin && Array.isArray(margin)) {
        margin.forEach((item, index) => {
            classes[`m${indexToBreakpoint(index)}-${sizeToClass(item)}`] = true;
        });
    } else if (margin) {
        classes[`m-${sizeToClass(margin)}`] = true;
    }

    // marginX
    if (marginX && Array.isArray(marginX)) {
        marginX.forEach((item, index) => {
            classes[`ml${indexToBreakpoint(index)}-${sizeToClass(item)}`] = true;
            classes[`mr${indexToBreakpoint(index)}-${sizeToClass(item)}`] = true;
        });
    } else if (marginX) {
        classes[`ml-${sizeToClass(marginX)}`] = true;
        classes[`mr-${sizeToClass(marginX)}`] = true;
    }

    // marginY
    if (marginY && Array.isArray(marginY)) {
        marginY.forEach((item, index) => {
            classes[`mt${indexToBreakpoint(index)}-${sizeToClass(item)}`] = true;
            classes[`mb${indexToBreakpoint(index)}-${sizeToClass(item)}`] = true;
        });
    } else if (marginY) {
        classes[`mt-${sizeToClass(marginY)}`] = true;
        classes[`mb-${sizeToClass(marginY)}`] = true;
    }

    // background
    if (background) {
        classes[background] = true;
    }

    // border
    if (border) {
        classes[`border-${border}`] = true;
    }

    // shadow
    if (shadow) {
        classes[`sarsa-shadow-${shadow}`] = true;
    }

    // alignX
    if (alignX) {
        classes[`has-display-flex`] = true;
        classes[`align-x-${alignX}`] = true;
    }

    // alignY
    if (alignY) {
        classes[`has-display-flex`] = true;
        classes[`align-y-${alignY}`] = true;
    }

    // textAlign
    if (textAlign) {
        classes[`text-align-${textAlign}`] = true;
    }

    return (
        <Tag
            data-component="Box"
            ref={innerRef}
            className={cx('sarsa-box', classes, className)}
            {...rest}
        >
            {children}
        </Tag>
    );
}

Box.propTypes = {
    /** Contents of Box */
    children: PropTypes.node,

    /** The rendered html tag */
    tag: PropTypes.string,

    /** Adds a drop shadow. Higher numbers are larger shadows */
    shadow: PropTypes.oneOf([1, 2, 3, 4]),

    /** Padding added to all sides */
    padding: SIZES_WITH_RESPONSIVE_OPTION,

    /** Padding added to the left and right sides */
    paddingX: SIZES_WITH_RESPONSIVE_OPTION,

    /** Padding added to the top and bottom */
    paddingY: SIZES_WITH_RESPONSIVE_OPTION,

    /** Margin added to all sides */
    margin: SIZES_WITH_RESPONSIVE_OPTION,

    /** Margin added to the left and right sides */
    marginX: SIZES_WITH_RESPONSIVE_OPTION,

    /** Margin added to the top and bottom */
    marginY: SIZES_WITH_RESPONSIVE_OPTION,

    /** Adds the specified background color */
    background: PropTypes.oneOf([
        'white',
        'gray-1',
        'gray-2',
        'light-blue',
        'extra-light-blue',
        'dark-blue',
        'blue-gray',
        'light-red',
        'light-yellow',
        'light-green',
    ]),

    /** Adds a 1px border of the specified color */
    border: PropTypes.oneOf(['gray', 'blue', 'yellow', 'red', 'green']),

    /** Sets the display to flex and sets justify-content */
    alignX: PropTypes.oneOf(['left', 'right', 'center', 'space-between', 'space-around']),

    /** Sets the display to flex and sets align-items */
    alignY: PropTypes.oneOf(['top', 'bottom', 'center', 'stretch', 'baseline']),

    /** Sets text-align */
    textAlign: PropTypes.oneOf(['left', 'right', 'center']),

    /** Additional class added to the outer container */
    className: PropTypes.string,

    innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
};
